import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, Stack, Typography } from "@mui/material";
import React from "react";
import Breadcrumb from "../Breadcrumb";
import Footer from "./Footer";
import WarningBanner from "../WarningBanner";
import useGlobalStore from "@shared/store/GlobalStore";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import { usePageVersion } from "@frontoffice/hooks/PageVersionHook";
import { useFooter, useMenu } from "@shared/hooks/SectionHook";
import { useSites } from "@shared/hooks/SiteHook";
const Layout = (props) => {
    const { children } = props;
    const { isBo } = useGlobalStore((state) => ({ isBo: state.isBo }));
    const { pathname } = useLocation();
    // Scroll to top when the pathname changes
    React.useEffect(() => {
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
    }, [pathname]);
    const { currentSite: site, isLoading: isSiteLoading } = useSites();
    const { isLoading: isFooterLoading } = useFooter(site?.id);
    const { isLoading: isMenuLoading } = useMenu(site?.id);
    const isLoading = isSiteLoading || isFooterLoading || isMenuLoading;
    const { currentPageVersion, loading: isPageLoading } = usePageVersion();
    const { title, shortDescription } = currentPageVersion || {};
    const templatesWithoutGenericHero = [
        "HOME",
        "PACK_CYBER",
        "MODULES_OUTILS",
        "PRESTATION_EXPERTES",
    ];
    const shouldDisplayGenericHero = !templatesWithoutGenericHero.includes(currentPageVersion?.template || "");
    const shouldApplyGenericHeroLayout = shouldDisplayGenericHero && !isPageLoading;
    const decodedShortDescription = React.useMemo(() => {
        if (!shortDescription) {
            return shortDescription;
        }
        try {
            return decodeURIComponent(shortDescription);
        }
        catch {
            return shortDescription;
        }
    }, [shortDescription]);
    const shortDescriptionText = decodedShortDescription
        ?.replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (isLoading)
        return null;
    return (_jsxs(Stack, { sx: {
            minHeight: "100vh",
            overflow: "hidden",
            ...(isBo
                ? {
                    contain: "layout",
                    maxWidth: "100vw",
                }
                : {}),
        }, children: [_jsxs(Stack, { sx: { flex: "1 0 auto" }, children: [_jsx(Header, { children: _jsx(WarningBanner, {}) }), _jsxs(Box, { component: "main", id: "main-content", tabIndex: -1, sx: {
                            pt: { xs: "72px", md: "88px" },
                            // Permet d'éviter le décalage du footer pendant le chargement, solution potentiellement temporaire
                            ...(isPageLoading &&
                                !isBo && {
                                minHeight: "100vh",
                                "@supports (height: 100dvh)": {
                                    minHeight: "100dvh",
                                },
                            }),
                        }, children: [shouldDisplayGenericHero && (_jsx(Box, { sx: {
                                    position: "relative",
                                    py: {
                                        xs: "40px",
                                        md: "64px",
                                    },
                                    backgroundImage: !isPageLoading
                                        ? {
                                            xs: "url('/pattern-mobile.svg')",
                                            md: "url('/pattern.svg')",
                                        }
                                        : undefined,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundColor: "background.header",
                                    minHeight: shouldApplyGenericHeroLayout
                                        ? { xs: "auto", md: "200px" }
                                        : "400px",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        background: "linear-gradient(90deg, hsla(240, 100%, 50%, 0.25) 0%, #0000FF 50%, hsla(240, 100%, 50%, 0.25) 100%)",
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        inset: 0,
                                    },
                                }, children: _jsx(Container, { sx: {
                                        "& *": {
                                            zIndex: 1,
                                            color: "primary.contrastText",
                                        },
                                        px: { xs: "24px !important", md: "100px !important" },
                                    }, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Breadcrumb, {}), _jsxs(Stack, { spacing: 3, children: [_jsx(Typography, { variant: "h1", sx: { color: "primary.contrastText" }, children: title }), shortDescriptionText && (_jsx(Typography, { variant: "subtitle1", component: "p", sx: {
                                                            color: "primary.contrastText",
                                                            maxWidth: "600px",
                                                            fontWeight: 400,
                                                        }, children: shortDescriptionText }))] })] }) }) })), shouldApplyGenericHeroLayout ? (_jsx(Container, { sx: {
                                    py: { xs: "16px", md: "56px" },
                                    px: { xs: "8px", md: "100px !important" },
                                }, children: children })) : (children)] })] }), _jsx(Footer, {})] }));
};
export default Layout;
